/**
 * Clientes / marcas del Design Calendar.
 * Especiales + marcas CRM (`m{ID}`) + legacy (tommy, zara…) para no romper datos viejos.
 */

export const DESIGN_CALENDAR_CLIENTES_DEFAULT = [
  {
    id: "todos",
    label: "Todos los clientes",
    color: "#a16207",
    fill: "#fde68a",
    kind: "special",
  },
  {
    id: "tommy",
    label: "Tommy (legado)",
    color: "#1d4ed8",
    fill: "#93c5fd",
    kind: "legacy",
  },
  {
    id: "eci",
    label: "ECI (legado)",
    color: "#15803d",
    fill: "#86efac",
    kind: "legacy",
  },
  {
    id: "zara",
    label: "Zara (legado)",
    color: "#be123c",
    fill: "#fda4af",
    kind: "legacy",
  },
  {
    id: "massimo",
    label: "Massimo Dutti (legado)",
    color: "#92400e",
    fill: "#fdba74",
    kind: "legacy",
  },
  {
    id: "scalpers",
    label: "Scalpers (legado)",
    color: "#6d28d9",
    fill: "#c4b5fd",
    kind: "legacy",
  },
  {
    id: "pepe",
    label: "Pepe Jeans (legado)",
    color: "#0369a1",
    fill: "#7dd3fc",
    kind: "legacy",
  },
  {
    id: "bimba",
    label: "Bimba y Lola (legado)",
    color: "#be185d",
    fill: "#f9a8d4",
    kind: "legacy",
  },
  {
    id: "silbon",
    label: "Silbon (legado)",
    color: "#c2410c",
    fill: "#fdba74",
    kind: "legacy",
  },
  {
    id: "karl",
    label: "Karl (legado)",
    color: "#0f766e",
    fill: "#5eead4",
    kind: "legacy",
  },
  {
    id: "otros",
    label: "Sin marca / Otros",
    color: "#475569",
    fill: "#cbd5e1",
    kind: "special",
  },
];

/** @deprecated */
export const DESIGN_CALENDAR_CLIENTES = DESIGN_CALENDAR_CLIENTES_DEFAULT;

const AUTO_PALETTE = [
  { color: "#be123c", fill: "#fda4af" },
  { color: "#1d4ed8", fill: "#93c5fd" },
  { color: "#15803d", fill: "#86efac" },
  { color: "#6d28d9", fill: "#c4b5fd" },
  { color: "#c2410c", fill: "#fdba74" },
  { color: "#0f766e", fill: "#5eead4" },
  { color: "#a16207", fill: "#fde68a" },
  { color: "#be185d", fill: "#f9a8d4" },
  { color: "#0369a1", fill: "#7dd3fc" },
  { color: "#7c3aed", fill: "#ddd6fe" },
  { color: "#b45309", fill: "#fcd34d" },
  { color: "#334155", fill: "#cbd5e1" },
];

function normalizeHex(value, fallback) {
  const raw = String(value || "").trim();
  if (/^#[0-9a-fA-F]{6}$/.test(raw)) return raw.toLowerCase();
  if (/^[0-9a-fA-F]{6}$/.test(raw)) return `#${raw.toLowerCase()}`;
  return fallback;
}

function hashHue(id) {
  let h = 0;
  const s = String(id || "");
  for (let i = 0; i < s.length; i += 1) {
    h = (h * 31 + s.charCodeAt(i)) >>> 0;
  }
  return AUTO_PALETTE[h % AUTO_PALETTE.length];
}

export function isMarcaClienteId(id) {
  return /^m\d{1,10}$/i.test(String(id || "").trim());
}

/**
 * Normaliza catálogo del API (marcas + especiales).
 * Si viene vacío, cae al default legacy.
 */
export function mergeClientesCatalog(rows) {
  if (!Array.isArray(rows) || !rows.length) {
    return DESIGN_CALENDAR_CLIENTES_DEFAULT.map((item) => ({ ...item }));
  }
  return rows.map((row) => {
    const id = String(row.id || row.Id || "")
      .trim()
      .toLowerCase();
    const auto = hashHue(id || "otros");
    return {
      id: id || "otros",
      label: String(row.label || row.Label || id || "Otros").trim() || "Otros",
      color: normalizeHex(row.color || row.Color, auto.color),
      fill: normalizeHex(row.fill || row.Fill, auto.fill),
      kind: row.kind || (isMarcaClienteId(id) ? "marca" : "special"),
      marcaId: row.marcaId ?? null,
    };
  });
}

export function clienteById(id, catalog = DESIGN_CALENDAR_CLIENTES_DEFAULT) {
  const key = String(id || "")
    .trim()
    .toLowerCase();
  const list = catalog?.length ? catalog : DESIGN_CALENDAR_CLIENTES_DEFAULT;
  const hit = list.find((c) => c.id === key);
  if (hit) return hit;

  const legacy = DESIGN_CALENDAR_CLIENTES_DEFAULT.find((c) => c.id === key);
  if (legacy) return legacy;

  if (isMarcaClienteId(key)) {
    const auto = hashHue(key);
    return {
      id: key,
      label: `Marca ${key.slice(1)}`,
      color: auto.color,
      fill: auto.fill,
      kind: "marca",
    };
  }

  return (
    list.find((c) => c.id === "otros") ||
    DESIGN_CALENDAR_CLIENTES_DEFAULT.find((c) => c.id === "otros") ||
    list[0]
  );
}

export function inferClienteFromTexto(texto) {
  const t = String(texto || "").toUpperCase();
  if (!t.trim()) return "otros";
  if (
    /\bSHOPPING\b/.test(t) ||
    /\bTODOS\b/.test(t) ||
    /\bALL TEAMS\b/.test(t) ||
    /\bALL CLIENTS\b/.test(t)
  ) {
    return "todos";
  }
  if (/\bTOMMY\b|\bTH\b|\bTH\s*\//.test(t)) return "tommy";
  if (/\bECI\b|EL CORTE/.test(t)) return "eci";
  if (/\bZARA\b/.test(t)) return "zara";
  if (/\bMASSIMO\b|\bDUTTI\b/.test(t)) return "massimo";
  if (/\bSCALPERS\b/.test(t)) return "scalpers";
  if (/\bPEPE\b/.test(t)) return "pepe";
  if (/\bBIMBA\b|\bLOLA\b/.test(t)) return "bimba";
  if (/\bSILBON\b/.test(t)) return "silbon";
  if (/\bKARL\b/.test(t)) return "karl";
  return "otros";
}

export function resolveClienteId(entry) {
  const stored = String(entry?.Cliente || entry?.cliente || "")
    .trim()
    .toLowerCase();
  if (stored && stored !== "otros") return stored;
  return inferClienteFromTexto(entry?.Texto || entry?.texto);
}

export function entryAccentColor(entry, catalog) {
  return clienteById(resolveClienteId(entry), catalog).color;
}

export function entryFillColor(entry, catalog) {
  const c = clienteById(resolveClienteId(entry), catalog);
  return c.fill || c.color;
}

/** Opciones del picker: catálogo + valor actual si es legado fuera de lista. */
export function pickerOptionsForValue(catalog, value) {
  const list = Array.isArray(catalog) ? [...catalog] : [];
  const key = String(value || "")
    .trim()
    .toLowerCase();
  if (key && !list.some((item) => item.id === key)) {
    list.push(clienteById(key, catalog));
  }
  return list;
}

export function matchesClienteFilter(entryCliente, selectedIds) {
  if (!selectedIds?.length) return true;
  const key = String(entryCliente || "")
    .trim()
    .toLowerCase();
  return selectedIds.includes(key);
}
